import React, { useEffect, useState, useMemo } from "react";
import { Table, Card, Typography, Divider, Tag, Input, Space } from "antd";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import ExcelExportButton from "./ExcelExportButton";
import { ExcelColumn } from "../../utils/excelExport";

const { Title } = Typography;

const StockReportTable: React.FC = () => {
    const { fetchStockReport } = useReportApi();
    const { t } = useTranslation();
    const [data, setData] = useState<any[]>([]);
    const [grandTotals, setGrandTotals] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Fetch stock report on mount
    useEffect(() => {
        setLoading(true);
        fetchStockReport()
            .then(res => {
                setData(res.products || []);
                setGrandTotals(res.grandTotals || {});
            })
            .finally(() => setLoading(false));
    }, [fetchStockReport]);

    const filteredData = useMemo(() => {
        if (!search) return data;
        const q = search.toLowerCase();
        return data.filter(p =>
            (p.name ?? "").toLowerCase().includes(q) ||
            (p.code ?? "").toLowerCase().includes(q)
        );
    }, [data, search]);

    // Get all currency/unit pairs in data (dynamic column groups)
    const allPairs = useMemo(() => {
        const pairs: { currency: string; unit: string }[] = [];
        const appendPairs = (obj: any) => {
            Object.entries(obj || {}).forEach(([currency, units]: any) => {
                Object.keys(units || {}).forEach(unit => {
                    if (!pairs.some(p => p.currency === currency && p.unit === unit)) {
                        pairs.push({ currency, unit });
                    }
                });
            });
        };
        filteredData.forEach(product => appendPairs(product.stockByCurrencyUnit));
        appendPairs(grandTotals);
        return pairs;
    }, [filteredData, grandTotals]);

    // Table columns
    const columns = [
        {
            title: t("product_code"),
            dataIndex: "code",
            fixed: "left" as const,
            width: 110,
            render: (code: string) => <Tag color="geekblue">{code}</Tag>
        },
        {
            title: t("product_name"),
            dataIndex: "name",
            fixed: "left" as const,
            width: 160,
            render: (name: string) => <b>{name}</b>
        },
        ...allPairs.map(({ currency, unit }) => ({
            title: (
                <span>
                    <Tag color="blue">{currency}</Tag>
                    <Tag color="purple">{unit}</Tag>
                </span>
            ),
            children: [
                {
                    title: t("qty"),
                    key: `qty_${currency}_${unit}`,
                    align: "right" as const,
                    width: 80,
                    render: (_: any, record: any) =>
                        record.stockByCurrencyUnit?.[currency]?.[unit]?.qty ?? 0
                },
                {
                    title: t("value"),
                    key: `value_${currency}_${unit}`,
                    align: "right" as const,
                    width: 110,
                    render: (_: any, record: any) =>
                        (record.stockByCurrencyUnit?.[currency]?.[unit]?.value ?? 0).toLocaleString()
                }
            ]
        }))
    ];

    // Grand total summary row
    const summaryRow = () => (
        <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
                <b>{t("grand_total")}</b>
            </Table.Summary.Cell>
            {allPairs.map(({ currency, unit }) => (
                <React.Fragment key={`${currency}_${unit}`}>
                    <Table.Summary.Cell align="right" index={2}>
                        <b>{grandTotals?.[currency]?.[unit]?.qty ?? 0}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right" index={3}>
                        <b>{(grandTotals?.[currency]?.[unit]?.value ?? 0).toLocaleString()}</b>
                    </Table.Summary.Cell>
                </React.Fragment>
            ))}
        </Table.Summary.Row>
    );

    // The grid is currency × unit, so the export mirrors it as one flat column
    // per pair rather than trying to nest headers a spreadsheet cannot show.
    const excelColumns: ExcelColumn<any>[] = [
        { header: t("product_code") as string, key: "code", width: 16 },
        { header: t("product_name") as string, key: "name", width: 28 },
        ...allPairs.flatMap(({ currency, unit }) => [
            {
                header: `${currency} ${unit} — ${t("qty")}`,
                key: (row: any) => row.stockByCurrencyUnit?.[currency]?.[unit]?.qty ?? 0,
                width: 16,
            },
            {
                header: `${currency} ${unit} — ${t("value")}`,
                key: (row: any) => row.stockByCurrencyUnit?.[currency]?.[unit]?.value ?? 0,
                width: 18,
            },
        ]),
    ];

    const excelFooter = [
        [
            t("grand_total") as string,
            "",
            ...allPairs.flatMap(({ currency, unit }) => [
                grandTotals?.[currency]?.[unit]?.qty ?? 0,
                grandTotals?.[currency]?.[unit]?.value ?? 0,
            ]),
        ],
    ];

    return (
        <Card
            title={<Title level={4}>{t("stock_report")}</Title>}
            bordered={false}
            style={{ margin: 24, borderRadius: 16, boxShadow: "0 2px 8px #f0f1f2" }}
        >
            <Space style={{ marginBottom: 16 }} wrap>
                <Input.Search
                    allowClear
                    placeholder={t("search_by_name_or_code") || "Search by name or code"}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width: 300 }}
                />
                <ExcelExportButton
                    fileName="stock-report"
                    sheetName={t("stock_report") as string}
                    columns={excelColumns}
                    rows={filteredData}
                    footer={excelFooter}
                    title={[t("stock_report") as string]}
                />
            </Space>
            <Table
                bordered
                loading={loading}
                dataSource={filteredData}
                columns={columns}
                rowKey={r => r.code}
                pagination={false}
                scroll={{ x: true }}
                summary={summaryRow}
                size="middle"
                style={{ background: "#fff", borderRadius: 12 }}
            />
            <Divider />
        </Card>
    );
};

export default StockReportTable;
